import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { BlockRenderer } from '@shared/BlockRenderer';
import PageHeader from '@shared/layouts/PageHeader';
import BottomNav from '@shared/layouts/BottomNav';
import { getUserInfo } from '@shared/api/client';
import { getLatestNewsDetail } from '@features/home/api/homeService';

const LatestNewsDetailPage = () => {
    const { postId } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [blocks, setBlocks] = useState([]);
    const [templateId, setTemplateId] = useState(6);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(() => getUserInfo());

    useEffect(() => {
        const onAuthChange = () => setUser(getUserInfo());
        window.addEventListener('auth-change', onAuthChange);
        return () => window.removeEventListener('auth-change', onAuthChange);
    }, []);

    // 套用字體（此頁面不在 PageLayoutWrapper 內）
    useEffect(() => {
        const saved = localStorage.getItem('preferred-font');
        const fontClass = saved === 'primary' ? 'font-round' : 'font-serif';
        document.body.classList.remove('font-round', 'font-serif');
        document.body.classList.add(fontClass);
    }, []);

    useEffect(() => {
        if (!postId) {
            setBlocks([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        getLatestNewsDetail(Number(postId))
            .then((res) => {
                setBlocks(Array.isArray(res.detail) ? res.detail : []);
                setTemplateId(res.template_id || 6);
            })
            .catch((err) => {
                console.error('Failed to fetch latest news detail:', err);
                setBlocks([]);
            })
            .finally(() => setLoading(false));
    }, [postId]);

    return (
        <div className="min-h-screen flex flex-col bg-white relative">
            <PageHeader user={user} showHome={true} />
            
            {/* Back Button (Absolute positioned to scroll away) */}
            <div className="absolute top-[80px] md:top-[76px] left-4 md:left-8 z-10">
                <button
                    onClick={() => navigate(-1)}
                    className="flex justify-center items-center w-10 h-10 rounded-full bg-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.1)] backdrop-blur text-[var(--color-primary)] hover:bg-white transition-colors cursor-pointer"
                    aria-label={t('latest-news.backAria')}
                >
                    <ChevronLeft size={24} className="mr-0.5" />
                </button>
            </div>
            <main className="flex-1 pt-[90px] md:pt-[86px] pb-24 md:pb-0">
                {loading ? (
                    <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    </div>
                ) : blocks.length === 0 ? (
                    <section className="py-20 px-6 bg-white">
                        <div className="max-w-5xl mx-auto text-center">
                            <div className="text-[var(--color-text-sub)] mb-4">
                                <span className="material-symbols-outlined text-6xl">article</span>
                            </div>
                            <p className="text-lg text-[var(--color-text-sub)]">{t('latest-news.noContent')}</p>
                        </div>
                    </section>
                ) : (
                    <BlockRenderer blocks={blocks} templateId={templateId} />
                )}
            </main>

            <BottomNav className="md:hidden" />
        </div>
    );
};

export default LatestNewsDetailPage;
